import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public NPS survey page at /nps/{token}. Anonymous — no auth.
 * Shows a 0-10 score picker + optional comment textarea. Already-responded
 * surveys still let the visitor change their answer (idempotent on backend).
 */
const NPSSurvey = () => {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/nps/${token}`)
      .then((r) => {
        setSurvey(r.data);
        if (r.data.score !== null && r.data.score !== undefined) {
          setScore(r.data.score);
          setComment(r.data.comment || '');
        }
      })
      .catch(() => toast.error('Survey link is invalid or expired'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (score === null) return toast.error('Pick a score 0-10');
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/nps/${token}`, {
        score, comment: comment.trim() || null,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md p-8 border-primary/40 bg-primary/5 text-center" data-testid="nps-thank-you">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h1 className="text-2xl font-black mb-3">Thank you!</h1>
          <p className="text-sm text-muted-foreground">
            Your feedback helps us serve you better. We read every comment.
          </p>
          <Button className="w-full mt-6" variant="outline" onClick={() => window.location.href = '/'}>
            Back to home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="nps-survey-page">
      <header className="border-b border-border bg-card/50 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="text-2xl font-black">
            NEXT<span className="text-primary">/</span>GEN
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            Customer Feedback
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black mb-3">How did we do?</h1>
          <p className="text-base text-muted-foreground">
            How likely are you to recommend NextGen Network Solutions to a friend or colleague?
          </p>
          {survey?.ticket_title && (
            <p className="text-xs text-muted-foreground mt-2 italic">
              Following up on: "{survey.ticket_title}"
            </p>
          )}
        </div>

        <Card className="p-6 border-border" data-testid="nps-score-picker">
          <div className="flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            <span>Not likely</span>
            <span>Very likely</span>
          </div>
          <div className="grid grid-cols-11 gap-1.5">
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setScore(i)}
                data-testid={`nps-score-${i}`}
                className={`aspect-square rounded-md border text-base font-bold transition-all ${
                  score === i
                    ? 'bg-primary text-primary-foreground border-primary scale-110'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
        </Card>

        {score !== null && (
          <Card className="p-6 border-border space-y-4" data-testid="nps-comment-card">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">
                {score >= 9 ? 'What worked well?' : score >= 7 ? 'What would make it a 10?' : 'How can we do better?'}
              </label>
              <Textarea
                rows={4}
                value={comment}
                maxLength={2000}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optional, but extremely helpful."
                className="bg-input mt-2"
                data-testid="nps-comment"
              />
            </div>
            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full font-bold uppercase tracking-widest"
              data-testid="nps-submit"
            >
              {submitting ? 'Submitting…' : 'Submit Feedback'}
            </Button>
          </Card>
        )}
      </main>
    </div>
  );
};

export default NPSSurvey;
